import React, { useId, useRef, useState } from "react";

import { ChevronDown, ChevronRight, Settings } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import {
  type CircuitView,
  type Metric,
  type StageBreakdown,
  type StageRow,
  type UdpPipelineSummary,
  type UdpPipelineView,
  formatMetric,
  metricHint,
  metricLabel,
  udpPipelineView,
} from "@/lib/udpPipelineMetrics";
import {
  UDP_PIPELINE_SECTIONS,
  type UdpPipelineBreakdownId,
  type UdpPipelineSectionId,
  useUdpPipelinePrefs,
} from "@/lib/udpPipelinePanelPrefs";
import { useT } from "@/app/localization";

/**
 * Live snapshot of the UDP ingestion pipeline from /api/stats. Display only: every
 * value is the backend's; "n/a" = the field is not in this snapshot. Collapse and
 * section visibility are this browser's local preference (localStorage).
 */
export function UdpPipelinePanel({ stats, snapshotTs }: { stats: unknown; snapshotTs?: string | null }) {
  const t = useT();
  const view = udpPipelineView(stats);
  const { prefs, setCollapsed, setSectionVisible, setBreakdownOpen, reset } = useUdpPipelinePrefs();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsButton = useRef<HTMLButtonElement>(null);
  const bodyId = useId();
  const settingsId = useId();
  const expanded = !prefs.collapsed;

  const closeSettings = () => {
    setSettingsOpen(false);
    settingsButton.current?.focus();
  };

  return (
    <Card data-testid="udp-pipeline-panel">
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <CardTitle className="text-sm">{t("operator.ops.udp.title")}</CardTitle>
            {expanded ? (
              <span className="text-xs opacity-50">
                {t("operator.ops.udp.source", { endpoint: "/api/stats", snapshot: snapshotTs ? "yes" : "no", time: snapshotTs ?? "" })}
              </span>
            ) : (
              <CollapsedSummary summary={view.summary} snapshotTs={snapshotTs} />
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              ref={settingsButton}
              variant="ghost"
              size="sm"
              aria-expanded={settingsOpen}
              aria-controls={settingsId}
              aria-label={t("operator.ops.udp.settings_label")}
              onClick={() => setSettingsOpen((o) => !o)}
            >
              <Settings size={14} />
              {t("operator.ops.udp.settings")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={expanded}
              aria-controls={bodyId}
              aria-label={expanded ? t("operator.ops.udp.collapse_label") : t("operator.ops.udp.expand_label")}
              onClick={() => setCollapsed(expanded)}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {expanded ? t("common.action.collapse") : t("common.action.expand")}
            </Button>
          </div>
        </div>

        {settingsOpen && (
          <fieldset
            id={settingsId}
            className="rounded-md border border-border px-3 pt-1 pb-3"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                closeSettings();
              }
            }}
          >
            <legend className="px-1 text-xs font-medium uppercase opacity-60">{t("operator.ops.udp.visible_sections")}</legend>
            <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2 xl:grid-cols-4">
              {UDP_PIPELINE_SECTIONS.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={prefs.sections[s.id]}
                    onChange={(e) => setSectionVisible(s.id, e.target.checked)}
                  />
                  {t(s.labelKey)}
                </label>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="xs" onClick={reset}>
                {t("operator.ops.udp.reset")}
              </Button>
              <Button variant="ghost" size="xs" onClick={closeSettings}>
                {t("operator.ops.udp.done")}
              </Button>
            </div>
          </fieldset>
        )}

        {expanded && (
          <div id={bodyId}>
            <PanelSections
              view={view}
              visible={prefs.sections}
              openBreakdowns={prefs.breakdowns}
              onBreakdownToggle={setBreakdownOpen}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CollapsedSummary({ summary, snapshotTs }: { summary: UdpPipelineSummary; snapshotTs?: string | null }) {
  const t = useT();
  const pressure = summary.pressureHigh;
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="udp-pipeline-summary">
      <span className="opacity-60">{t("operator.ops.udp.snapshot", { snapshot: snapshotTs ? "yes" : "no", time: snapshotTs ?? "" })}</span>
      <span>
        <span className="opacity-60">{t("operator.ops.udp.summary_queue")}</span>{" "}
        <span className="tabular-nums">{formatMetric(summary.queueCurrent, t)}</span>
      </span>
      <span>
        <span className="opacity-60">{t("operator.ops.udp.summary_dropped_errors")}</span>{" "}
        <span className="tabular-nums">
          {formatMetric(summary.dropped, t)} / {formatMetric(summary.processingErrors, t)}
        </span>
      </span>
      <Badge variant={pressure === null ? "neutral" : pressure ? "error" : "success"}>
        {t("operator.ops.udp.pressure", { level: pressure === null ? "unknown" : pressure ? "high" : "normal" })}
      </Badge>
    </span>
  );
}

function PanelSections({ view, visible, openBreakdowns, onBreakdownToggle }: {
  view: UdpPipelineView;
  visible: Record<UdpPipelineSectionId, boolean>;
  openBreakdowns: Record<UdpPipelineBreakdownId, boolean>;
  onBreakdownToggle: (id: UdpPipelineBreakdownId, open: boolean) => void;
}) {
  const t = useT();
  const hp = view.hotPath;
  // An open breakdown is a table: give the section the whole row.
  const wide = hp.breakdowns.some((b) => openBreakdowns[b.parent]);
  if (!Object.values(visible).some(Boolean)) {
    return <div className="text-xs opacity-60">{t("operator.ops.udp.all_hidden")}</div>;
  }
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {visible.queue && <Section title={t("operator.ops.udp.title_queue")} metrics={view.queue} />}

      {visible.redis && (
        <Section title={t("operator.ops.udp.title_redis")} metrics={view.redis}>
          <div className="flex flex-wrap gap-2 pt-1">
            {view.circuits.map((c) => <CircuitBadge key={c.plane} circuit={c} />)}
          </div>
        </Section>
      )}

      {visible.sampleProjection && (
        <Section
          title={t("operator.ops.udp.title_sample_projection")}
          badge={view.sampleProjectionMode ?? t("operator.ops.udp.mode_na")}
          metrics={view.sampleProjection}
        />
      )}

      {visible.flusherStages && (
        <Section title={t("operator.ops.udp.title_flusher_stages")} metrics={view.flusherStages} />
      )}

      {visible.hotPath && (
        <div data-testid="hot-path-section" className={wide ? "md:col-span-2 xl:col-span-3" : undefined}>
          <SectionTitle title={t("operator.ops.udp.title_hot_path")} />
          {hp.enabled === null ? (
            <div className="text-xs opacity-60">{t("operator.ops.udp.not_in_snapshot")}</div>
          ) : hp.enabled === false ? (
            <div className="text-xs opacity-70">
              {t("operator.ops.udp.sampling_off", { setting: "HOT_PATH_STAGE_SAMPLE_EVERY = 0" })}
            </div>
          ) : (
            <>
              <MetricList
                metrics={[
                  { label: "Sample every", labelKey: "operator.ops.udp.sample_every", value: hp.sampleEvery, unit: "packets" },
                  { label: "Packets timed", labelKey: "operator.ops.udp.packets_timed", value: hp.packetsTimed },
                  { label: "Total (top-level stages)", labelKey: "operator.ops.udp.total_top_level", value: hp.cpuUsPerPacketTotal, unit: "us_per_packet" },
                ]}
              />
              <div className="mt-2">
                <MetricList metrics={hp.stages} />
              </div>
              <HotPathBreakdowns view={view} open={openBreakdowns} onToggle={onBreakdownToggle} />
            </>
          )}
        </div>
      )}

      {visible.backgroundPressure && (
        <Section title={t("operator.ops.udp.title_background_pressure")} metrics={view.backgroundPressure} />
      )}

      {visible.sessionProcessor && (
        <Section title={t("operator.ops.udp.title_session_processor")} metrics={view.sessionProcessor} />
      )}

      {visible.gateEval && (
        <Section title={t("operator.ops.udp.title_gate_eval")} metrics={view.gateEval}>
          <div className="mt-2 text-xs opacity-50">
            {t("operator.ops.udp.gate_eval_note")}
          </div>
          <MetricList metrics={view.gateEvalOutcomes} empty={t("operator.ops.udp.not_in_snapshot")} />
        </Section>
      )}
    </div>
  );
}

function HotPathBreakdowns({ view, open, onToggle }: {
  view: UdpPipelineView;
  open: Record<UdpPipelineBreakdownId, boolean>;
  onToggle: (id: UdpPipelineBreakdownId, open: boolean) => void;
}) {
  const t = useT();
  const hp = view.hotPath;
  if (hp.breakdowns.length === 0) {
    return (
      <div className="mt-2 text-xs opacity-60" data-testid="hot-path-breakdown-na">
        {hp.breakdownEnabled === false
          ? t("operator.ops.udp.breakdown_off", { setting: "HOT_PATH_STAGE_BREAKDOWN = False" })
          : t("operator.ops.udp.breakdown_na")}
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2">
      {hp.breakdowns.map((b) => (
        <BreakdownBlock
          key={b.parent}
          breakdown={b}
          open={open[b.parent]}
          onToggle={(next) => onToggle(b.parent, next)}
          gateReadOutcomes={b.parent === "command" ? hp.gateReadOutcomes : undefined}
        />
      ))}
      <div className="text-xs opacity-50">
        {t("operator.ops.udp.breakdown_note")}
        {hp.lapCostUs !== null && hp.lapsPerTimedPacket !== null
          ? ` ${t("operator.ops.udp.instrumentation", { laps: formatMetric(hp.lapsPerTimedPacket, t), cost: formatMetric(hp.lapCostUs, t) })}`
          : ""}
      </div>
    </div>
  );
}

function BreakdownBlock({ breakdown, open, onToggle, gateReadOutcomes }: {
  breakdown: StageBreakdown;
  open: boolean;
  onToggle: (open: boolean) => void;
  gateReadOutcomes?: Metric[];
}) {
  const t = useT();
  const bodyId = useId();
  const title = t("operator.ops.udp.breakdown_title", { stage: breakdown.parent });
  return (
    <div className="rounded-md border border-border" data-testid={`breakdown-${breakdown.parent}`}>
      <button
        type="button"
        className="flex w-full items-center gap-1 px-2 py-1 text-left text-xs font-medium"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => onToggle(!open)}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {title}
        <span className="ml-auto opacity-50">
          {t("operator.ops.udp.stage_count", { count: breakdown.common.length + breakdown.rare.length })}
        </span>
      </button>
      {open && (
        <div id={bodyId} className="overflow-x-auto px-2 pb-2">
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="text-left opacity-60">
                <th className="py-0.5 pr-2 font-medium">{t("operator.ops.udp.col_stage")}</th>
                <th className="px-1 text-right font-medium">{t("operator.ops.udp.col_cpu_avg")}</th>
                <th className="px-1 text-right font-medium">{t("operator.ops.udp.col_cpu_max")}</th>
                <th className="px-1 text-right font-medium">{t("operator.ops.udp.col_wall_avg")}</th>
                <th className="px-1 text-right font-medium">{t("operator.ops.udp.col_wall_max")}</th>
                <th className="px-1 text-right font-medium">{t("operator.ops.udp.col_cpu_per_packet")}</th>
                <th className="px-1 text-right font-medium">{t("operator.ops.udp.col_wall_per_packet")}</th>
                <th className="pl-1 text-right font-medium">{t("operator.ops.udp.col_samples")}</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.common.map((r) => <StageRowView key={r.name} row={r} />)}
              <tr data-stage="unattributed" className="opacity-60">
                <td className="py-0.5 pr-2 italic">{t("operator.ops.udp.unattributed")}</td>
                <td colSpan={4} />
                <MetricCell value={breakdown.unattributedCpuUsPerPacket} col="cpu_us_per_packet" />
                <MetricCell value={breakdown.unattributedWallUsPerPacket} col="wall_us_per_packet" />
                <td />
              </tr>
              {breakdown.rare.length > 0 && (
                <tr>
                  <td colSpan={8} className="pt-2 pb-0.5 text-xs font-medium uppercase opacity-50">
                    {t("operator.ops.udp.rare_stages")}
                  </td>
                </tr>
              )}
              {breakdown.rare.map((r) => <StageRowView key={r.name} row={r} />)}
            </tbody>
          </table>
          {gateReadOutcomes && gateReadOutcomes.length > 0 && (
            <div className="mt-2" data-testid="gate-read-outcomes">
              <div className="mb-1 text-xs font-medium uppercase opacity-60">
                {t("operator.ops.udp.gate_read_outcomes")}
              </div>
              <MetricList metrics={gateReadOutcomes} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StageRowView({ row }: { row: StageRow }) {
  return (
    <tr data-stage={row.name}>
      <td className="py-0.5 pr-2 opacity-80">{row.name}</td>
      <MetricCell value={row.cpuUsAvg} col="cpu_us_avg" />
      <MetricCell value={row.cpuUsMax} col="cpu_us_max" />
      <MetricCell value={row.wallUsAvg} col="wall_us_avg" />
      <MetricCell value={row.wallUsMax} col="wall_us_max" />
      <MetricCell value={row.cpuUsPerPacket} col="cpu_us_per_packet" />
      <MetricCell value={row.wallUsPerPacket} col="wall_us_per_packet" />
      <MetricCell value={row.samples} col="samples" />
    </tr>
  );
}

function MetricCell({ value, col }: { value: number | null; col: string }) {
  const t = useT();
  return (
    <td className={`px-1 text-right ${value === null ? "opacity-40" : ""}`} data-col={col}>
      {formatMetric(value, t)}
    </td>
  );
}

function SectionTitle({ title, badge }: { title: string; badge?: string }) {
  return (
    <div className="mb-1 flex items-center gap-2">
      <span className="text-xs font-medium uppercase opacity-60">{title}</span>
      {badge && <Badge variant="neutral">{badge}</Badge>}
    </div>
  );
}

function Section({ title, badge, metrics, children }: {
  title: string; badge?: string; metrics: Metric[]; children?: React.ReactNode;
}) {
  return (
    <div>
      <SectionTitle title={title} badge={badge} />
      <MetricList metrics={metrics} />
      {children}
    </div>
  );
}

function MetricList({ metrics, empty }: { metrics: Metric[]; empty?: string }) {
  const t = useT();
  if (metrics.length === 0) {
    return <div className="text-xs opacity-60">{empty ?? t("operator.ops.udp.na")}</div>;
  }
  return (
    <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 text-sm">
      {metrics.map((m) => {
        const hint = metricHint(t, m);
        return (
          <React.Fragment key={m.label}>
            <dt className="truncate opacity-70" title={hint}>
              {metricLabel(t, m)}
              {hint && <span className="ml-1 text-xs opacity-60">({hint})</span>}
            </dt>
            <dd
              className={`text-right tabular-nums ${m.value === null ? "opacity-40" : ""}`}
              data-metric={m.label}
            >
              {formatMetric(m.value, t)}
              {m.value !== null && m.unit ? <span className="ml-1 text-xs opacity-50">{t("operator.ops.udp.unit", { unit: m.unit })}</span> : null}
            </dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
}

function CircuitBadge({ circuit }: { circuit: CircuitView }) {
  const t = useT();
  const params = { plane: circuit.plane, state: circuit.state ?? "", opens: circuit.openCount ?? 0 };
  if (circuit.shared) {
    return <Badge variant="neutral">{t("operator.ops.udp.breaker", { ...params, status: "shared" })}</Badge>;
  }
  if (circuit.state === null) {
    return <Badge variant="neutral">{t("operator.ops.udp.breaker", { ...params, status: "unknown" })}</Badge>;
  }
  const variant = circuit.state === "closed" ? "success" : circuit.state === "open" ? "error" : "warning";
  return (
    <Badge variant={variant}>
      {t("operator.ops.udp.breaker", { ...params, status: circuit.openCount === null ? "reported" : "reported_opens" })}
    </Badge>
  );
}
